import { jsPDF } from 'jspdf'

import type {
  BuildingInfo,
  CurrentStateDescription,
  EnergyLabel,
  EnergySource,
  FirestoreCustomPackageDocument,
  RetrofitName,
  Substance,
} from '@/shared/types'
import { getRetrofitDisplayName } from '@/shared/utils/displayMappings'
import { ENERGY_LABEL_COLORS } from '@/shared/utils/energyLabelColors'
import { getEnergySourceLabel } from '@/shared/utils/isfpEnumLabels'

interface PDFExportData {
  buildingInfo?: BuildingInfo | null
  currentRating: EnergyLabel
  potentialRating?: EnergyLabel
  annualEnergyDemand?: string
  annualEnergyConsumption?: string
  customPackage?: FirestoreCustomPackageDocument | null
  buildingImageUrl?: string
  substance?: Substance
  currentStateDescription?: CurrentStateDescription
}

const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num)

const hexToRGB = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0]
}

// Helper function to load and crop image with rounded corners
const loadImageWithRoundedCorners = (
  imageUrl: string,
  width: number,
  height: number,
  borderRadius: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      // Create canvas
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }

      // Set canvas size
      canvas.width = width
      canvas.height = height

      // Calculate aspect-fill dimensions
      const imgAspect = img.width / img.height
      const canvasAspect = width / height

      let drawWidth, drawHeight, offsetX, offsetY

      if (imgAspect > canvasAspect) {
        // Image is wider than canvas
        drawHeight = height
        drawWidth = img.width * (height / img.height)
        offsetX = (width - drawWidth) / 2
        offsetY = 0
      } else {
        // Image is taller than canvas
        drawWidth = width
        drawHeight = img.height * (width / img.width)
        offsetX = 0
        offsetY = (height - drawHeight) / 2
      }

      // Draw rounded rectangle clipping path
      ctx.beginPath()
      ctx.moveTo(borderRadius, 0)
      ctx.lineTo(width - borderRadius, 0)
      ctx.quadraticCurveTo(width, 0, width, borderRadius)
      ctx.lineTo(width, height - borderRadius)
      ctx.quadraticCurveTo(width, height, width - borderRadius, height)
      ctx.lineTo(borderRadius, height)
      ctx.quadraticCurveTo(0, height, 0, height - borderRadius)
      ctx.lineTo(0, borderRadius)
      ctx.quadraticCurveTo(0, 0, borderRadius, 0)
      ctx.closePath()
      ctx.clip()

      // Draw image with aspect-fill
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)

      // Convert to data URL with PNG for transparency and lossless quality
      resolve(canvas.toDataURL('image/png', 1.0))
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    img.src = imageUrl
  })
}

export const exportCurrentRatingToPDF = async (data: PDFExportData): Promise<void> => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 20
  let yPosition = margin

  // Helper function to add a new page if needed
  const checkAndAddPage = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      pdf.addPage()
      yPosition = margin
      return true
    }
    return false
  }

  // Title
  pdf.setFontSize(24)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0, 0, 0)
  pdf.text('Aktuelle ', margin, yPosition)
  const titleWidth = pdf.getTextWidth('Aktuelle ')
  pdf.setTextColor(91, 192, 107) // #5BC06B
  pdf.text('Bewertung', margin + titleWidth, yPosition)
  yPosition += 15

  // Building Overview Section
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0, 0, 0)
  pdf.text('Gebäudeübersicht', margin, yPosition)
  yPosition += 8

  // Building info grid
  if (data.buildingInfo) {
    const gridItems = [
      {
        label: 'Baujahr',
        value: data.buildingInfo.constructionYear?.toString() || '–',
      },
      {
        label: 'Wohneinheiten',
        value: data.buildingInfo.residentialUnits?.toString() || '–',
      },
      {
        label: 'Heizung',
        value: data.buildingInfo.heatingEnergySource
          ? data.buildingInfo.heatingEnergySource
              .split(', ')
              .map(source => getEnergySourceLabel(source.trim() as EnergySource))
              .join(', ')
          : '–',
      },
      {
        label: 'Warmwasser',
        value: data.buildingInfo.hotWaterEnergySource
          ? data.buildingInfo.hotWaterEnergySource
              .split(', ')
              .map(source => getEnergySourceLabel(source.trim() as EnergySource))
              .join(', ')
          : '–',
      },
      {
        label: 'Energieverbrauch',
        value: data.annualEnergyConsumption || '–',
        unit: 'kWh/Jahr',
      },
      {
        label: 'Energiebedarf',
        value: data.annualEnergyDemand || '–',
        unit: 'kWh/Jahr',
      },
    ]

    pdf.setFontSize(9)
    const imageWidth = 60 // Width for building image if available
    const imageGap = 10 // Gap between grid and image
    const hasImage = !!data.buildingImageUrl
    const availableGridWidth = hasImage
      ? pageWidth - 2 * margin - imageWidth - imageGap
      : pageWidth - 2 * margin - 10
    const itemsPerRow = 2
    const itemWidth = availableGridWidth / itemsPerRow
    const itemHeight = 12

    // Add building image if available
    if (hasImage && data.buildingImageUrl) {
      const imageX = pageWidth - margin - imageWidth
      const imageY = yPosition - 8 // Move image up
      const imageHeight = 45 // Approximate aspect ratio
      const borderRadius = 24 // Border radius in pixels for high-res canvas
      try {
        // Convert mm to pixels for canvas at 3x resolution (300 DPI equivalent)
        // This maintains high quality when the PDF scales it down
        const scaleFactor = 3
        const pixelWidth = Math.round(imageWidth * 3.7795275591 * scaleFactor)
        const pixelHeight = Math.round(imageHeight * 3.7795275591 * scaleFactor)

        // Load image with rounded corners and aspect-fill at high resolution
        const croppedImageData = await loadImageWithRoundedCorners(
          data.buildingImageUrl,
          pixelWidth,
          pixelHeight,
          borderRadius
        )

        // Add the cropped image to PDF
        pdf.addImage(croppedImageData, 'PNG', imageX, imageY, imageWidth, imageHeight)
      } catch (error) {
        console.error('Failed to add building image to PDF:', error)
      }
    }

    gridItems.forEach((item, index) => {
      const col = index % itemsPerRow
      const row = Math.floor(index / itemsPerRow)
      const x = margin + col * (itemWidth + 5)
      const y = yPosition + row * (itemHeight + 3)

      pdf.setFont('helvetica', 'normal')
      pdf.setTextColor(70, 74, 84) // #464A54
      pdf.text(item.label, x, y)

      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(0, 0, 0)
      const valueText = item.unit ? `${item.value} ${item.unit}` : item.value
      pdf.text(valueText, x, y + 4)
    })

    yPosition += Math.ceil(gridItems.length / itemsPerRow) * (itemHeight + 3) + 10
  }

  // Building Potential Section
  checkAndAddPage(80)
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0, 0, 0)
  pdf.text('Ihr Gebäude-Potenzial', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(7)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(116, 119, 127) // #74777F
  pdf.text(
    'Berechnet mit technisch sinnvollen Maßnahmen mit kurzer Amortisationszeit*',
    margin,
    yPosition
  )
  yPosition += 8

  // Energy Class Scale
  pdf.setFontSize(12)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0, 0, 0)
  pdf.text('Energieklasse', margin, yPosition)

  // Add "Bedarf" badge next to title
  const energyClassTitleWidth = pdf.getTextWidth('Energieklasse')
  const badgePadding = 2
  const badgeText = 'Bedarf'
  pdf.setFontSize(8)
  const badgeTextWidth = pdf.getTextWidth(badgeText)
  const badgeWidth = badgeTextWidth + badgePadding * 2
  const badgeHeight = 3.5
  const badgeX = margin + energyClassTitleWidth + 4
  const badgeY = yPosition - 2.75

  pdf.setFillColor(245, 245, 247) // #F5F5F7
  pdf.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 1, 1, 'F')
  pdf.setTextColor(70, 74, 84) // #464A54
  pdf.setFont('helvetica', 'normal')
  pdf.text(badgeText, badgeX + badgePadding, yPosition + 0.2)

  pdf.setTextColor(0, 0, 0)
  yPosition += 8

  const energyLabels: EnergyLabel[] = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
  const widthPercents = [20, 30, 40, 50, 60, 70, 80, 90, 100]
  const barHeight = 4
  const maxBarWidth = 120
  const barGap = 0.5

  // Helper function to draw arrow-shaped energy label
  // clipPath: polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)
  const drawEnergyArrow = (x: number, y: number, width: number, height: number) => {
    const arrowIndent = 2 // 8px in web, scaled down to 2mm for PDF
    pdf.lines(
      [
        [width - arrowIndent, 0], // to top-right before arrow (x + width - indent, y)
        [arrowIndent, height / 2], // to arrow point (x + width, y + height/2)
        [-arrowIndent, height / 2], // to bottom-right before arrow (x + width - indent, y + height)
        [-(width - arrowIndent), 0], // to bottom-left (x, y + height)
        [0, -height], // close path back to start (x, y)
      ],
      x,
      y,
      [1, 1],
      'F'
    )
  }

  // Helper function to draw arrow-shaped indicator
  // clipPath: polygon(28% 0%, 100% 0%, 100% 100%, 28% 100%, 0% 50%)
  const drawIndicatorArrow = (x: number, y: number, width: number, height: number) => {
    const leftIndent = width * 0.28 // 28% of width
    pdf.lines(
      [
        [leftIndent, -height / 2], // to top-left after arrow tail
        [width - leftIndent, 0], // to top-right
        [0, height], // to bottom-right
        [-(width - leftIndent), 0], // to bottom-left after arrow tail
        [-leftIndent, -height / 2], // back to arrow tail point
      ],
      x,
      y + height / 2,
      [1, 1],
      'F'
    )
  }

  energyLabels.forEach((label, index) => {
    const barWidth = (widthPercents[index] / 100) * maxBarWidth
    const y = yPosition + index * (barHeight + barGap)

    const color = hexToRGB(ENERGY_LABEL_COLORS[label])
    pdf.setFillColor(color[0], color[1], color[2])
    drawEnergyArrow(margin, y, barWidth, barHeight)

    pdf.setFontSize(7)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.text(label, margin + 2, y + 3)

    // Add current rating indicator
    if (label === data.currentRating) {
      const indicatorWidth = 8
      const indicatorHeight = barHeight + 1
      pdf.setFillColor(24, 29, 41) // #181D29
      drawIndicatorArrow(margin + barWidth + 2, y - 0.5, indicatorWidth, indicatorHeight)

      pdf.setTextColor(255, 255, 255)
      pdf.setFontSize(7)
      pdf.text(label, margin + barWidth + 4.5, y + 3)

      pdf.setTextColor(0, 0, 0)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Aktuelle', margin + barWidth + 12, y + 3)
    }

    // Add potential rating indicator
    if (data.potentialRating && label === data.potentialRating && label !== data.currentRating) {
      const indicatorWidth = 8
      const indicatorHeight = barHeight + 1
      const potColor = hexToRGB(ENERGY_LABEL_COLORS[data.potentialRating])
      pdf.setFillColor(potColor[0], potColor[1], potColor[2])
      drawIndicatorArrow(margin + barWidth + 2, y - 0.5, indicatorWidth, indicatorHeight)

      pdf.setTextColor(255, 255, 255)
      pdf.setFontSize(7)
      pdf.text(label, margin + barWidth + 4.5, y + 3)

      pdf.setTextColor(0, 0, 0)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Potenzial', margin + barWidth + 12, y + 3)
    }
  })

  yPosition += energyLabels.length * (barHeight + barGap) + 10

  // Savings Potential
  if (data.customPackage) {
    checkAndAddPage(40)
    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.text('Einsparpotenzial', margin, yPosition)

    // Add "Verbrauch" badge next to title
    const savingsTitleWidth = pdf.getTextWidth('Einsparpotenzial')
    const savingsBadgePadding = 2
    const savingsBadgeText = 'Verbrauch'
    pdf.setFontSize(8)
    const savingsBadgeTextWidth = pdf.getTextWidth(savingsBadgeText)
    const savingsBadgeWidth = savingsBadgeTextWidth + savingsBadgePadding * 2
    const savingsBadgeHeight = 3.5
    const savingsBadgeX = margin + savingsTitleWidth + 4
    const savingsBadgeY = yPosition - 2.75

    pdf.setFillColor(245, 245, 247) // #F5F5F7
    pdf.roundedRect(savingsBadgeX, savingsBadgeY, savingsBadgeWidth, savingsBadgeHeight, 1, 1, 'F')
    pdf.setTextColor(70, 74, 84) // #464A54
    pdf.setFont('helvetica', 'normal')
    pdf.text(savingsBadgeText, savingsBadgeX + savingsBadgePadding, yPosition + 0.2)

    pdf.setTextColor(0, 0, 0)
    yPosition += 6

    const savings = data.customPackage.data.consumptionBasedCalculation.economics
    let costValue = '– €'
    let currencySavings = savings.avgEnergyCostSavingsInCtWithinFiveYears
    if (typeof currencySavings === 'number') {
      currencySavings = currencySavings / 100
      if (currencySavings >= 0) {
        const lower = Math.round(currencySavings * 0.9)
        const upper = Math.round(currencySavings * 1.1)
        costValue = `${formatNumber(lower)} – ${formatNumber(upper)} €`
      } else {
        costValue = '< 0 €'
      }
    }

    pdf.setFontSize(14)
    pdf.text(`${costValue} /Jahr`, margin, yPosition)
    yPosition += 6

    const percentage =
      savings.avgEnergyCostSavingsInPctWithinFiveYears !== undefined
        ? `${savings.avgEnergyCostSavingsInPctWithinFiveYears.toFixed(2)} %`
        : '– %'
    pdf.setFontSize(8)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(70, 74, 84)
    pdf.text(`Bis zu ${percentage} der Energiekosten pro Jahr sparen`, margin, yPosition)
    yPosition += 10

    // Property Value Increase
    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.text('Immobilienwertsteigerung', margin, yPosition)
    yPosition += 6

    const propertyIncrease =
      data.customPackage.data.demandBasedCalculation.economics.realEstateValuationIncreaseInPct
    const percentageDisplay =
      propertyIncrease !== undefined ? `${propertyIncrease.toFixed(2)} %` : '– %'
    pdf.setFontSize(14)
    pdf.text(percentageDisplay, margin, yPosition)
    yPosition += 12
  }

  // Proposed Measures Section
  if (data.customPackage) {
    checkAndAddPage(60)
    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.text('Vorgeschlagene Sanierungsmaßnahmen*', margin, yPosition)
    yPosition += 8

    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'normal')
    data.customPackage.measures.forEach((measure: RetrofitName, index: number) => {
      const displayName = getRetrofitDisplayName(measure)
      const col = index % 2
      const row = Math.floor(index / 2)
      const x = margin + col * 90
      const y = yPosition + row * 6

      pdf.text(`• ${displayName}`, x, y)
    })

    yPosition += Math.ceil(data.customPackage.measures.length / 2) * 6 + 8

    // Financial Details
    const { economics } = data.customPackage.data.consumptionBasedCalculation
    const { amortizationTimeInYears } = economics
    let amortizationText = 'k. A.'
    if (amortizationTimeInYears) {
      amortizationText =
        amortizationTimeInYears > 40 ? '+40 Jahre' : `${amortizationTimeInYears} Jahre`
    }

    const renovationCost = Math.round(economics.retrofitCostsInCt.mean / 100).toLocaleString(
      'de-DE'
    )
    const maxFunding = Math.round(
      (economics.retrofitCostsInCt.mean / 100) * (economics.subsidyRateInPct / 100)
    ).toLocaleString('de-DE')
    const costsAfterFunding = Math.round(
      (economics.retrofitCostsInCt.mean / 100) * (1 - economics.subsidyRateInPct / 100)
    ).toLocaleString('de-DE')

    const financialItems = [
      { label: 'Amortisationszeit', value: amortizationText },
      { label: 'Sanierungskosten', value: `Ab ${renovationCost} €` },
      { label: 'max. Förderung', value: `${maxFunding} €` },
      { label: 'Kosten nach Förderung', value: `Bis zu ${costsAfterFunding} €` },
    ]

    checkAndAddPage(30)
    pdf.setFontSize(9)
    financialItems.forEach((item, index) => {
      const col = index % 2
      const row = Math.floor(index / 2)
      const x = margin + col * 90
      const y = yPosition + row * 10

      pdf.setFont('helvetica', 'normal')
      pdf.setTextColor(70, 74, 84)
      pdf.text(item.label, x, y)

      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(0, 0, 0)
      pdf.text(item.value, x, y + 4)
    })

    yPosition += Math.ceil(financialItems.length / 2) * 10 + 10
  }

  // Current State Section
  if (data.currentStateDescription) {
    checkAndAddPage(80)
    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.text('Ist Zustand', margin, yPosition)
    yPosition += 10

    // Calculate U-values for components
    type ComponentWithUValue = { uValue?: number }
    const calculateUValue = <T extends ComponentWithUValue>(
      components: T[]
    ): number | [number, number] | undefined => {
      if (components.length === 0) return undefined

      const uValues = components
        .map(comp => comp.uValue)
        .filter((uValue): uValue is number => uValue !== undefined)

      const uniqueUValues = Array.from(new Set(uValues))

      if (uniqueUValues.length === 0) return undefined
      if (uniqueUValues.length === 1) return uniqueUValues[0]
      return [Math.min(...uniqueUValues), Math.max(...uniqueUValues)]
    }

    const uValuesAvailable = {
      roof: calculateUValue([
        ...(data.substance?.roofComponents || []),
        ...(data.substance?.topFloorCeilingComponents || []),
      ]),
      walls: calculateUValue([
        ...(data.substance?.exteriorWallComponents || []),
        ...(data.substance?.basementExteriorWallComponents || []),
      ]),
      windows: calculateUValue([
        ...(data.substance?.windowComponents || []),
        ...(data.substance?.doorComponents || []),
        ...(data.substance?.roofWindowComponents || []),
      ]),
      basement: calculateUValue([
        ...(data.substance?.basementCeilingComponents || []),
        ...(data.substance?.floorPlateComponents || []),
      ]),
      basementStairway: undefined,
    }

    // Building Components
    pdf.setFontSize(12)
    pdf.text('Bauteile', margin, yPosition)
    yPosition += 6

    const components = [
      {
        label: 'Dach / oberer Gebäudeabschluss',
        key: 'upperCompletion' as const,
        uValueKey: 'roof' as const,
      },
      { label: 'Wände', key: 'exteriorWalls' as const, uValueKey: 'walls' as const },
      { label: 'Fenster', key: 'windows' as const, uValueKey: 'windows' as const },
      {
        label: 'Keller / unterer Gebäudeabsschluss',
        key: 'lowerCompletion' as const,
        uValueKey: 'basement' as const,
      },
      {
        label: 'Kellerabgang',
        key: 'basementEntrance' as const,
        uValueKey: 'basementStairway' as const,
      },
    ]

    pdf.setFontSize(9)
    components.forEach(component => {
      const description = data.currentStateDescription?.[component.key]
      const uValue = uValuesAvailable[component.uValueKey]

      if (description) {
        checkAndAddPage(15)
        pdf.setFont('helvetica', 'bold')
        pdf.setTextColor(0, 0, 0)

        let labelText = component.label
        if (uValue) {
          const uValueText = Array.isArray(uValue)
            ? `(U-Wert: ${formatNumber(uValue[0])} – ${formatNumber(uValue[1])})`
            : `(U-Wert: ${formatNumber(uValue)})`
          labelText = `${component.label} ${uValueText}`
        }

        pdf.text(labelText, margin, yPosition)
        yPosition += 4

        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(70, 74, 84)
        const cleanDescription: string = description.replace(/<br>/g, ' ')
        const lines = pdf.splitTextToSize(cleanDescription, pageWidth - 2 * margin) as string[]
        pdf.text(lines, margin, yPosition)
        yPosition += lines.length * 4 + 4
      }
    })

    // System Technology
    checkAndAddPage(40)
    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)
    pdf.text('Anlagentechnik', margin, yPosition)
    yPosition += 6

    const systems = [
      { label: 'Heizung', key: 'heating' as const },
      { label: 'Wärmeverteilung', key: 'heatingTransfer' as const },
      { label: 'Warmwasser', key: 'drinkingHotWater' as const },
      { label: 'Lüftung', key: 'ventilation' as const },
    ]

    pdf.setFontSize(9)
    systems.forEach(system => {
      const description = data.currentStateDescription?.[system.key]
      if (description) {
        checkAndAddPage(15)
        pdf.setFont('helvetica', 'bold')
        pdf.setTextColor(0, 0, 0)
        pdf.text(system.label, margin, yPosition)
        yPosition += 4

        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(70, 74, 84)
        const cleanDescription: string = description.replace(/<br>/g, ' ')
        const lines = pdf.splitTextToSize(cleanDescription, pageWidth - 2 * margin) as string[]
        pdf.text(lines, margin, yPosition)
        yPosition += lines.length * 4 + 4
      }
    })
  }

  // Save PDF
  const fileName = `Aktuelle_Bewertung_${new Date().toISOString().split('T')[0]}.pdf`
  pdf.save(fileName)
}
